import React from 'react';
import { createPortal } from 'react-dom';
import { useUserPrefs } from '../../hooks/useUserPrefs';

export type GuideTourStep<TTarget extends string = string> = {
  key: string;
  title: string;
  instruction: string;
  tip?: string;
  target: TTarget;
};

export type GuideTargetResolver<TTarget extends string = string> = (target: TTarget) => HTMLElement | null;

type UseGuideTourOptions<TTarget extends string = string> = {
  steps: Array<GuideTourStep<TTarget>>;
  prefPath: string;
  storageKey: string;
  version: string;
  enabled?: boolean;
  ready?: boolean;
  autoOpen?: boolean;
  resolveTarget: GuideTargetResolver<TTarget>;
  onStepChange?: (step: GuideTourStep<TTarget>) => void;
};

export function buildGuideSeenMarker(version: string, seenAt: string): string {
  const normalizedVersion = String(version || '').trim();
  return normalizedVersion ? `${normalizedVersion}:${seenAt}` : seenAt;
}

export function isGuideSeenMarkerCurrent(marker: string | null | undefined, version: string): boolean {
  const value = String(marker || '').trim();
  if (!value) return false;
  const normalizedVersion = String(version || '').trim();
  return normalizedVersion ? value.startsWith(`${normalizedVersion}:`) : true;
}

function readStorage(storageKey: string): string {
  if (!storageKey || typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(storageKey) || '';
  } catch {
    return '';
  }
}

function writeStorage(storageKey: string, value: string): void {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    if (window.localStorage.getItem(storageKey) !== value) {
      window.localStorage.setItem(storageKey, value);
    }
  } catch {
    // Ignore localStorage failures (private mode, quota, etc.).
  }
}

function currentGuideMarker(marker: string | null | undefined, version: string): string {
  return isGuideSeenMarkerCurrent(marker, version) ? String(marker || '').trim() : '';
}

function isVisible(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  const styles = window.getComputedStyle(element);
  return styles.visibility !== 'hidden' && styles.display !== 'none';
}

export function useGuideTour<TTarget extends string = string>({
  steps,
  prefPath,
  storageKey,
  version,
  enabled = true,
  ready = true,
  autoOpen = false,
  resolveTarget,
  onStepChange,
}: UseGuideTourOptions<TTarget>) {
  const { get, set, loaded, flush } = useUserPrefs();
  const [open, setOpen] = React.useState(false);
  const [stepIndex, setStepIndex] = React.useState(0);
  const [spotlightRect, setSpotlightRect] = React.useState<DOMRect | null>(null);
  const rafRef = React.useRef<number | null>(null);

  const activeStep = open ? (steps[stepIndex] || null) : null;
  const canLaunch = Boolean(enabled && ready && steps.length > 0);

  const readPrefMarker = React.useCallback(() => {
    if (!prefPath || !loaded) return '';
    const value = get<string>(prefPath, '');
    return typeof value === 'string' ? value : (value ? String(value) : '');
  }, [get, loaded, prefPath]);

  const hasSeenCurrentGuide = React.useCallback(() => {
    return (
      isGuideSeenMarkerCurrent(readPrefMarker(), version) ||
      isGuideSeenMarkerCurrent(readStorage(storageKey), version)
    );
  }, [readPrefMarker, storageKey, version]);

  const markSeen = React.useCallback(() => {
    const prefMarker = currentGuideMarker(readPrefMarker(), version);
    const storageMarker = currentGuideMarker(readStorage(storageKey), version);
    const marker = prefMarker || storageMarker || buildGuideSeenMarker(version, new Date().toISOString());
    writeStorage(storageKey, marker);
    if (prefPath && loaded && prefMarker !== marker) {
      set(prefPath, marker);
      if (typeof window !== 'undefined') {
        window.setTimeout(() => flush(), 0);
      } else {
        flush();
      }
    }
  }, [flush, loaded, prefPath, readPrefMarker, set, storageKey, version]);

  React.useEffect(() => {
    if (!loaded || !prefPath) return;
    const prefMarker = readPrefMarker();
    const storageMarker = readStorage(storageKey);
    if (isGuideSeenMarkerCurrent(prefMarker, version)) {
      writeStorage(storageKey, prefMarker);
      return;
    }
    if (isGuideSeenMarkerCurrent(storageMarker, version)) {
      set(prefPath, storageMarker);
      if (typeof window !== 'undefined') window.setTimeout(() => flush(), 0);
    }
  }, [flush, loaded, prefPath, readPrefMarker, set, storageKey, version]);

  const cancelMeasure = React.useCallback(() => {
    if (rafRef.current != null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = null;
  }, []);

  const measure = React.useCallback(() => {
    if (!open || !activeStep || typeof window === 'undefined') {
      setSpotlightRect(null);
      return;
    }
    const target = resolveTarget(activeStep.target);
    if (!target || !isVisible(target)) {
      setSpotlightRect(null);
      return;
    }
    setSpotlightRect(target.getBoundingClientRect());
  }, [activeStep, open, resolveTarget]);

  const updateSpotlight = React.useCallback(() => {
    cancelMeasure();
    if (typeof window === 'undefined') {
      measure();
      return;
    }
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        measure();
      });
    });
  }, [cancelMeasure, measure]);

  const close = React.useCallback(() => {
    cancelMeasure();
    setOpen(false);
    setSpotlightRect(null);
  }, [cancelMeasure]);

  const start = React.useCallback(() => {
    if (!canLaunch) return;
    setStepIndex(0);
    setOpen(true);
    markSeen();
  }, [canLaunch, markSeen]);

  const next = React.useCallback(() => {
    if (!steps.length) return;
    setStepIndex((prev) => {
      if (prev >= steps.length - 1) {
        close();
        return prev;
      }
      return prev + 1;
    });
  }, [close, steps.length]);

  const prev = React.useCallback(() => {
    setStepIndex((current) => Math.max(0, current - 1));
  }, []);

  React.useEffect(() => {
    if (!autoOpen || !canLaunch || !loaded || open) return;
    if (hasSeenCurrentGuide()) return;
    setStepIndex(0);
    setOpen(true);
    markSeen();
  }, [autoOpen, canLaunch, hasSeenCurrentGuide, loaded, markSeen, open]);

  React.useEffect(() => {
    if (!open || !activeStep) return;
    onStepChange?.(activeStep);
  }, [activeStep, onStepChange, open]);

  React.useEffect(() => {
    if (!open || !activeStep) return;
    const target = resolveTarget(activeStep.target);
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
    }
    updateSpotlight();
  }, [activeStep, open, resolveTarget, updateSpotlight]);

  React.useEffect(() => {
    if (!open) return;
    const sync = () => updateSpotlight();
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
    };
  }, [open, updateSpotlight]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, open]);

  React.useEffect(() => {
    return () => cancelMeasure();
  }, [cancelMeasure]);

  React.useEffect(() => {
    if (!open) return;
    if (!steps.length) {
      close();
      return;
    }
    if (stepIndex >= steps.length) {
      setStepIndex(Math.max(0, steps.length - 1));
    }
  }, [close, open, stepIndex, steps.length]);

  return {
    activeStep,
    canLaunch,
    close,
    isFinalStep: activeStep ? stepIndex >= steps.length - 1 : false,
    next,
    open,
    prev,
    setStepIndex,
    spotlightRect,
    start,
    stepCountLabel: activeStep ? `${stepIndex + 1} of ${steps.length}` : '',
    stepIndex,
  };
}

type GuideQuestionButtonProps = {
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  title?: string;
  ariaLabel?: string;
};

export const GuideQuestionButton = React.forwardRef<HTMLButtonElement, GuideQuestionButtonProps>(function GuideQuestionButton({
  onClick,
  disabled = false,
  className = '',
  title = 'Open guide',
  ariaLabel = 'Open guide',
}, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={['guide-question-button', className].filter(Boolean).join(' ')}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
    >
      ?
    </button>
  );
});

type GuideTourOverlayProps<TTarget extends string = string> = {
  activeStep: GuideTourStep<TTarget>;
  ariaLabel: string;
  isFinalStep: boolean;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  spotlightRect: DOMRect | null;
  stepCountLabel: string;
  stepIndex: number;
};

export function GuideTourOverlay<TTarget extends string = string>({
  activeStep,
  ariaLabel,
  isFinalStep,
  onClose,
  onNext,
  onPrev,
  spotlightRect,
  stepCountLabel,
  stepIndex,
}: GuideTourOverlayProps<TTarget>) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="guided-tour-overlay" role="dialog" aria-modal="true" aria-label={ariaLabel}>
      <button
        type="button"
        className="guided-tour-overlay__backdrop"
        aria-label={`Close ${ariaLabel}`}
        onClick={onClose}
      />
      {spotlightRect ? (
        <div
          className="guided-tour-overlay__spotlight"
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: Math.max(8, spotlightRect.left - 8),
            top: Math.max(8, spotlightRect.top - 8),
            width: spotlightRect.width + 16,
            height: spotlightRect.height + 16,
          }}
        />
      ) : null}
      <section className={`guided-tour-card${spotlightRect ? '' : ' guided-tour-card--centered'}`}>
        <div className="stack gap-xs">
          <div className="text-xs fg-muted guided-tour-card__step-label">
            Guided tour - {stepCountLabel}
          </div>
          <h3 className="h4 m0">{activeStep.title}</h3>
          <p className="text-sm m0">{activeStep.instruction}</p>
          {activeStep.tip ? (
            <p className="text-xs fg-muted m0">Tip: {activeStep.tip}</p>
          ) : null}
          <div className="flex between center wrap gap-xs guided-tour-card__actions">
            <button
              type="button"
              className="btn xs subtle"
              onClick={onPrev}
              disabled={stepIndex <= 0}
            >
              Back
            </button>
            <button type="button" className="btn xs subtle" onClick={onClose}>
              Skip
            </button>
            <button type="button" className="btn xs" onClick={onNext}>
              {isFinalStep ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
}
